import Vue from 'vue';
import {
    GET_DIARY,
    GET_DIARY_WEEK,
    SET_ANALYSIS_COMMENTS,
    SET_DAILY_COMMENTS,
    UPDATE_DAILY_COMMENTS,
    SET_DIARY,
    SET_DIARY_ENTRIES,
    SET_DIARY_WEEK,
    SET_MY_ANALYSIS_COMMENT,
    UPDATE_ANALYSIS_COMMENTS,
    SET_MY_DAILY_COMMENT,
    SET_COMMENT,
    PUBLISH_DIARY,
    STORE_COMMENT,
    SET_ENTRY,
    SET_COURSE_MEALS,
    SET_COURSE_SETTINGS,
    SET_COURSE_STATUSES,
    UPDATE_ENTRIES_STATUSES,
    ADD_ABSENCE,
    SET_DIARY_MEDIA,
    ADD_DIARY_MEDIA,
    UPLOAD_DIARY_MEDIA,
    UPLOAD_DIARY_DOCUMENT,
    UPDATE_DIARY_FILE,
    REMOVE_DIARY_MEDIA,
    DELETE_DIARY_FILE,
    CREATE_DIARY,
    CREATE_AND_PUBLISH_DIARY,
    SET_DIARY_ERROR,
    SET_DIARY_DOCUMENTS,
    ADD_DIARY_DOCUMENT,
    DELETE_DIARY_DOCUMENT,
    REMOVE_DIARY_DOCUMENT,
    SET_DIARY_HOLIDAYS,
    SET_DIARY_INTEGRATIONS,
    LIKE_DIARY,
    SET_DIARY_LIKES,
    SET_DAILY_NOTES,
    UPDATE_DAILY_NOTES,
    SET_MY_DAILY_NOTES,
    SAVE_ENTRY_STATUS,
    SAVE_ENTRY_MEAL,
    SET_ENTRY_MEALS,
    SAVE_ENTRY_FEEDBACK,
    UPDATE_ENTRY_FEEDBACK,
    DELETE_ENTRY_FEEDBACK,
    REMOVE_ENTRY_FEEDBACK,
    SET_ENTRY_FEEDBACK,
    START_ENTRY_TIME,
    STOP_ENTRY_TIME,
    ADD_ENTRY_TIME,
    UPDATE_ENTRY_TIME,
    DELETE_ENTRY_TIME,
    REMOVE_ENTRY_TIME,
    SET_ENTRY_TIME,
    SET_LEAVE_TYPE,
    UPDATE_ENTRY_LEAVE_STATUS,
    UPDATE_ENTRIES_LEAVE_STATUSES
} from './actions';
import DiaryService from '@services/diary.service';
import {SET_DIARY_FEEDBACK_TYPES} from '@/store/modules/course/actions';
import {convertFromUtc} from '@/filters';

// Keeps the API's UTC values under `*_utc`: this object is the one its action
// returns and whispers on to the other clients, and mobile parses the times as
// strict timestamp values rather than rendering them as-is.
const convertTimesFromUtc = (data) => {
    if (data.started_at.length > 5) {
        data.started_at_utc = data.started_at;
        data.ended_at_utc = data.ended_at ?? null;
        const convertedStartTime = convertFromUtc(data.started_at);
        data.started_at = Vue.moment(convertedStartTime).format('HH:mm');
        if (data.ended_at) {
            const convertedEndTime = convertFromUtc(data.ended_at);
            data.ended_at = Vue.moment(convertedEndTime).format('HH:mm');
        }
    }
};

const getDefaultState = () => {
    return {
        diary: {},
        texts: [],
        dailyComments: [],
        myDailyComment: null,
        analysisComments: [],
        myAnalysisComment: null,
        diaryNotes: [],
        myDiaryNotes: null,
        entries: [],
        media: [],
        documents: [],
        diary_week: [],
        leaveTypeOption: {},
        course_settings: {},
        course_meals: {},
        course_statuses: [] ,
        error: null,
        holidays: [],
        diary_likes: {
            liked: false,
            users: []
        },
        integrations: []
    };
};

const state = getDefaultState();

const mutations = {
    [SET_DIARY](state, data) {
        state.diary = data;
    },
    [SET_DIARY_ENTRIES](state, data) {
        data.forEach(e => {
            e.time_logs?.forEach(l => {
                convertTimesFromUtc(l);
            });
        });
        state.entries = data;
    },
    [SET_DAILY_COMMENTS](state, data) {
        Vue.set(state, 'dailyComments', [...data]);
    },
    [UPDATE_DAILY_COMMENTS](state, data) {
        const index = state.dailyComments.findIndex(comment => comment.id === data.id);
        if(index !== -1) {
            Vue.set(state.dailyComments, index, data);
        } else {
            state.dailyComments.push(data);
        }
    },
    [SET_MY_DAILY_COMMENT](state, content) {
        Vue.set(state, 'myDailyComment', {...content});
    },
    [SET_ANALYSIS_COMMENTS](state, data) {
        Vue.set(state, 'analysisComments', [...data]);
    },
    [SET_ENTRY_MEALS](state, {childId, data}) {
        const index = state.entries.findIndex(
            entry => entry.child_id === childId
        );
        if (index !== -1) {
            Vue.set(state.entries[index], 'meals', data);
        }
    },
    [SET_ENTRY_FEEDBACK](state, {childId, data}) {
        const index = state.entries.findIndex(
            entry => entry.child_id === childId
        );
        if (index !== -1) {
            const entry = state.entries.find(entry => entry.child_id === childId);
            const feedbackIndex = entry.feedback.findIndex(
              fb => fb.id === data.id
            );
            if (feedbackIndex !== -1) {
                Vue.set(state.entries[index].feedback, feedbackIndex, data);
            } else {
                entry.feedback.unshift(data);
                Vue.set(state.entries, index, entry);
            }
        }
    },
    [REMOVE_ENTRY_FEEDBACK](state, {childId, feedbackId}) {
        const index = state.entries.findIndex(
            entry => entry.child_id === childId
        );
        if (index !== -1) {
            const entry = state.entries.find(entry => entry.child_id === childId);
            const feedbackIndex = entry.feedback.findIndex(
              fb => fb.id === feedbackId
            );
            entry.feedback.splice(feedbackIndex, 1);
            Vue.set(state.entries, index, entry);
        }
    },
    [SET_ENTRY_TIME](state, {childId, data}) {
        const index = state.entries.findIndex(
          entry => entry.child_id === childId
        );
        if (index !== -1) {
            convertTimesFromUtc(data);
            const entry = state.entries.find(entry => entry.child_id === childId);
            const logIndex = entry.time_logs.findIndex(
              log => log.id === data.id
            );
            if (logIndex !== -1) {
                Vue.set(state.entries[index].time_logs, logIndex, data);
            } else {
                entry.time_logs.push(data);
                Vue.set(state.entries, index, entry);
            }
        }
    },
    [REMOVE_ENTRY_TIME](state, {childId, logId}) {
        const index = state.entries.findIndex(
            entry => entry.child_id === childId
        );
        if (index !== -1) {
            const entry = state.entries.find(entry => entry.child_id === childId);
            const logIndex = entry.time_logs.findIndex(
              log => log.id === logId
            );
            entry.time_logs.splice(logIndex, 1);
            Vue.set(state.entries, index, entry);
        }
    },
    [UPDATE_ANALYSIS_COMMENTS](state, data) {
        const index = state.analysisComments.findIndex(comment => comment.id === data.id);
        if (index !== -1) {
            Vue.set(state.analysisComments, index, data);
        } else {
            state.analysisComments.push(data);
        }
    },
    [SET_MY_ANALYSIS_COMMENT](state, content) {
        Vue.set(state, 'myAnalysisComment', {...content});
    },
    [SET_DAILY_NOTES](state, data) {
        Vue.set(state, 'diaryNotes', data);
    },
    [UPDATE_DAILY_NOTES](state, data) {
        const index = state.diaryNotes.findIndex(note => note.id === data.id);
        if (index !== -1) {
            Vue.set(state.diaryNotes, index, data);
        } else {
            state.diaryNotes.push(data);
        }
    },
    [SET_MY_DAILY_NOTES](state, content) {
        Vue.set(state, 'myDiaryNotes', content);
    },
    [SET_ENTRY](state, data) {
        data.time_logs?.forEach(l => {
            convertTimesFromUtc(l);
        });
        const index = state.entries.findIndex(
            entry => entry.child.id === data.child_id
        );
        if(index !== -1) {
            Vue.set(state.entries, index, data);
        }
    },
    [SET_COMMENT](state, data) {
        const index = state.texts.findIndex(text => text.id === data.id);
        if(index !== -1) {
            Vue.set(state.texts, index, data);
        } else {
            state.texts.push(data);
        }
    },
    [SET_DIARY_WEEK](state, data) {
        state.diary_week = data;
    },
    [SET_COURSE_SETTINGS](state, settings) {
        state.course_settings = settings;
    },
    [SET_COURSE_STATUSES](state, statuses) {
        state.course_statuses = statuses;
    },
    [SET_COURSE_MEALS](state, meals) {
        state.course_meals = meals;
    },
    [ADD_ABSENCE](state, attendance) {
        const index = state.entries.findIndex(
            entry => entry.child_id === attendance.child_id
        );
        if(index !== -1) {
            const entry = state.entries.find(
                entry => entry.child_id === attendance.child_id
            );
            entry.child.attendance = attendance;
            Vue.set(state.entries, index, entry);
        }
    },
    [SET_DIARY_MEDIA](state, media) {
      state.media = media;
    },
    [ADD_DIARY_MEDIA](state, media) {
        state.media.push(media);
    },
    [UPDATE_DIARY_FILE](state, media) {
        const index = state.media.findIndex(f => f.filename === media.filename);
        if(index !== -1) {
            Vue.set(state.media, index, media);
        }
    },
    [REMOVE_DIARY_MEDIA](state, fileName) {
        if (state.media.some(f => f.filename === fileName)) {
            state.media.splice(
                state.media.indexOf(state.media.find(f => f.filename === fileName)), 1
            );
        }
    },
    [REMOVE_DIARY_DOCUMENT](state, fileName) {
        if (state.documents.some(f => f.filename === fileName)) {
            state.documents.splice(
                state.documents.indexOf(state.documents.find(f => f.filename === fileName)), 1
            );
        }
    },
    [SET_DIARY_ERROR](state, error) {
        state.error = error;
    },
    [SET_DIARY_DOCUMENTS](state, documents) {
      state.documents = documents;
    },
    [ADD_DIARY_DOCUMENT](state, document) {
        state.documents.push(document);
    },
    [SET_DIARY_HOLIDAYS](state, holidays) {
        state.holidays = holidays;
    },
    [SET_DIARY_INTEGRATIONS](state, integrations) {
        state.integrations = integrations;
    },
    [SET_DIARY_LIKES](state, likes) {
        state.diary_likes = likes;
    },
    [SET_LEAVE_TYPE](state, leaveType) {
      state.leaveTypeOption = leaveType;
  }
};

const actions = {
    [GET_DIARY]({commit, getters}, data) {
        commit('START_CONTENT_LOADER');
        commit(SET_DIARY_ERROR, null);
        return DiaryService.getDiaryData(data).then(day => {
            const foreignDailyComments = [];
            let myDailyComment = {};
            const foreignAnalysisComments = [];
            let myAnalysisComment = {};
            const foreignDailyNotes = [];
            let myDailyNotes = {};
            let media = [];
            let documents = [];

            if(typeof day.daily_comments !== 'undefined') {
                day.daily_comments.map(c => {
                    if(c.created_by !== getters.currentEmployee.id) {
                        foreignDailyComments.push(c);
                    } else {
                        myDailyComment = c;
                    }
                });
            }

            if(typeof day.analysis_comments !== 'undefined') {
                day.analysis_comments.map(c => {
                    if(c.created_by !== getters.currentEmployee.id) {
                        foreignAnalysisComments.push(c);
                    } else {
                        myAnalysisComment = c;
                    }
                });
            }
            if(typeof day.daily_notes !== 'undefined') {
                day.daily_notes.map(c => {
                    if(c.created_by !== getters.currentEmployee.id) {
                        foreignDailyNotes.push(c);
                    } else {
                        myDailyNotes = c;
                    }
                });
            }

            if(typeof day.entries !== 'undefined') {
                commit(SET_DIARY_ENTRIES, day.entries);
            }

            if(typeof day.settings !== 'undefined') {
                commit(SET_COURSE_SETTINGS, day.settings);
            }

            if(typeof day.statuses !== 'undefined') {
                commit(SET_COURSE_STATUSES, day.statuses);
            }

            if(typeof day.meals !== 'undefined') {
                commit(SET_COURSE_MEALS, day.meals);
            }

            if(typeof  day.feedback_type_options !== 'undefined') {
                commit(SET_DIARY_FEEDBACK_TYPES, day.feedback_type_options);
            }

            if(typeof day.images !== 'undefined') {
                media = day.images;
            }

            if(day.documents) {
                documents = day.documents;
            }

            if (typeof day.likes !== 'undefined') {
                commit(SET_DIARY_LIKES, day.likes);
            } else {
                commit(SET_DIARY_LIKES, getDefaultState()['diary_likes']);
            }

            if (typeof day.integrations !== 'undefined') {
                commit(SET_DIARY_INTEGRATIONS, day.integrations);
            }

            if (typeof day.leave_type_option !== 'undefined') {
                commit(SET_LEAVE_TYPE, day.leave_type_option);
            }

            commit(SET_DIARY, day.diary);
            commit(SET_DAILY_COMMENTS, foreignDailyComments);
            commit(SET_MY_DAILY_COMMENT, myDailyComment);
            commit(SET_ANALYSIS_COMMENTS, foreignAnalysisComments);
            commit(SET_MY_ANALYSIS_COMMENT, myAnalysisComment);
            commit(SET_DAILY_NOTES, foreignDailyNotes);
            commit(SET_MY_DAILY_NOTES, myDailyNotes);
            commit(SET_DIARY_MEDIA, media);
            commit(SET_DIARY_DOCUMENTS, documents);
            commit(SET_DIARY_HOLIDAYS, day.holidays);

        }).catch(error => {
            commit(SET_DIARY_ERROR, error?.response?.data?.message || 'Network Error');
        }).finally(() => {
            commit('STOP_CONTENT_LOADER');
        });
    },
    [CREATE_DIARY]({commit, getters}) {
        if (!getters.diary.id) {
            return DiaryService.createDiary(getters.activeKindergartenId, getters.diary).then(res => {
                commit('SET_DIARY', res.diary);
                return res;
            });
        } else {
            return new Promise((resolve) => {
                resolve(getters.diary);
            });
        }
    },
    async [STORE_COMMENT]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        data.diary = getters.diary;
        return DiaryService.saveComment(getters.activeKindergartenId, data).then((res) => {
            commit(SET_COMMENT, res);
            const myComment = {
                comment: res.comment,
                creator: res.creator,
                id: res.id
            };
            if (res.type === 1) {
                commit(SET_MY_DAILY_COMMENT, myComment);
            }
            if (res.type === 2) {
                commit(SET_MY_ANALYSIS_COMMENT, myComment);
            }
            if (res.type === 3) {
                commit(SET_MY_DAILY_NOTES, myComment);
            }
            return res;
        });
    },
    async [SAVE_ENTRY_STATUS]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        return DiaryService.updateEntryStatus(getters.activeKindergartenId, getters.diary.id, data).then(res => {
            commit(SET_ENTRY, res);
            return res;
        });
    },
    [SAVE_ENTRY_MEAL]({commit, getters}, data) {
        return DiaryService.updateEntryMeal(getters.activeKindergartenId, getters.diary.id, data).then(res => {
            commit(SET_ENTRY_MEALS, {childId: data.child_id, data: res});
            return {childId: data.child_id, data: res};
        });
    },
    async [SAVE_ENTRY_FEEDBACK]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        return DiaryService.saveEntryFeedback(getters.activeKindergartenId, getters.diary.id, data).then(res => {
          commit(SET_ENTRY_FEEDBACK, {childId: data.child_id, data: res});
          return {childId: data.child_id, data: res};
        });
    },
    [UPDATE_ENTRY_FEEDBACK]({commit, getters}, data) {
        return DiaryService.updateEntryFeedback(getters.activeKindergartenId, getters.diary.id, data.feedbackId, data.form).then(res => {
          commit(SET_ENTRY_FEEDBACK, {childId: data.form.child_id, data: res});
          return {childId: data.form.child_id, data: res};
        });
    },
    [DELETE_ENTRY_FEEDBACK]({commit, getters}, data) {
        return DiaryService.deleteEntryFeedback(getters.activeKindergartenId, getters.diary.id, data.feedbackId).then((res) => {
          if (res) {
            commit(REMOVE_ENTRY_FEEDBACK, {childId: data.childId, feedbackId: data.feedbackId});
            return {childId: data.childId, feedbackId: data.feedbackId};
          }
        });
    },
    async [UPDATE_ENTRIES_STATUSES]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        const diaryId = getters.diary.id;
        return DiaryService.updateEntriesStatuses(getters.activeKindergartenId, diaryId, data).then(res => {
            commit(SET_DIARY_ENTRIES, res);
            return res;
        });
    },
    [CREATE_AND_PUBLISH_DIARY]({commit, getters}) {
        const diaryClone = {...getters.diary};
        diaryClone.published = 1;

        return DiaryService.createDiary(getters.activeKindergartenId, diaryClone).then(res => {
            commit('SET_DIARY', res.diary);
            return res;
        });
    },
    [PUBLISH_DIARY]({commit, getters}) {
        return DiaryService.publish(getters.activeKindergartenId, getters.diary.id).then(res => {
            commit(SET_DIARY, res);
            return res;
        });
    },
    [GET_DIARY_WEEK]({commit}, data) {
        commit('START_CONTENT_LOADER');
        commit(SET_DIARY_ERROR, null);
        DiaryService.getDiaryWeekData(data).then(res => {
            commit(SET_DIARY_WEEK, res);
        }).catch(error => {
            commit(SET_DIARY_ERROR, error?.response?.data?.message || 'Network Error');
        }).finally(() => {
            commit('STOP_CONTENT_LOADER');
        });
    },
    [UPLOAD_DIARY_MEDIA]({commit, getters}, data) {
        return DiaryService.uploadMedia(getters.activeKindergartenId, data.diary, data.form).then(res => {
            commit(ADD_DIARY_MEDIA, res.file);
            return res;
        });
    },
    [UPLOAD_DIARY_DOCUMENT]({commit, getters}, data) {
        return DiaryService.uploadDocument(getters.activeKindergartenId, data.diary, data.form).then(res => {
            commit(ADD_DIARY_DOCUMENT, res.file);
            return res;
        });
    },
    [UPDATE_DIARY_FILE]({commit, getters}, form) {
        return DiaryService.updateDiaryFile(getters.activeKindergartenId, form.filename, form).then(res => {
            commit(UPDATE_DIARY_FILE, res);
        });
    },
    [DELETE_DIARY_FILE]({commit, getters}, {fileName}) {
        return DiaryService.deleteDiaryFile(getters.activeKindergartenId, fileName).then(() => {
            commit(REMOVE_DIARY_MEDIA, fileName);
        });
    },
    [DELETE_DIARY_DOCUMENT]({commit, getters}, filename) {
        return DiaryService.deleteDiaryFile(getters.activeKindergartenId, filename).then(() => {
            commit(REMOVE_DIARY_DOCUMENT, filename);
        });
    },
    [LIKE_DIARY]({getters}, diaryId) {
        return DiaryService.likeDiary(getters.activeKindergartenId, diaryId).then(res => {
            return res;
        });
    },
    async [START_ENTRY_TIME]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        data.diary = getters.diary;
        return DiaryService.startEntryTime(getters.activeKindergartenId, data.diary_id, data).then(res => {
            commit(SET_ENTRY, res);
            return res;
        });
    },
    [STOP_ENTRY_TIME]({commit, getters}, data) {
        return DiaryService.stopEntryTime(getters.activeKindergartenId, data.diary_id, data).then(res => {
            commit(SET_ENTRY, res);
            return res;
        });
    },
    [ADD_ENTRY_TIME]({commit, getters}, data) {
      return DiaryService.addEntryTime(getters.activeKindergartenId, getters.diary.id, data).then(res => {
          commit(SET_ENTRY_TIME, {childId: data.child_id, data: res});
          return {childId: data.child_id, data: res};
      });
    },
    [UPDATE_ENTRY_TIME]({commit, getters}, data) {
      return DiaryService.updateEntryTime(getters.activeKindergartenId, getters.diary.id, data.logId, data.form).then(res => {
          commit(SET_ENTRY_TIME, {childId: data.childId, data: res});
          return {childId: data.childId, data: res};
      });
    },
    [DELETE_ENTRY_TIME]({commit, getters}, data) {
      return DiaryService.deleteEntryTime(getters.activeKindergartenId, getters.diary.id, data.logId).then(res => {
        if (res) {
            commit(REMOVE_ENTRY_TIME, {childId: data.childId, logId: data.logId});
            return {childId: data.childId, logId: data.logId};
        }
      });
    },
    [UPDATE_ENTRY_LEAVE_STATUS]({commit, getters}, data) {
      return DiaryService.updateEntryLeaveStatus(getters.activeKindergartenId, getters.diary.id, data).then(res => {
          commit(SET_ENTRY, res);
          return res;
      });
    },
    async [UPDATE_ENTRIES_LEAVE_STATUSES]({commit, dispatch, getters}, data) {
        await dispatch(CREATE_DIARY);
        const diaryId = getters.diary.id;
        return DiaryService.updateEntriesLeaveStatuses(getters.activeKindergartenId, diaryId, data).then(res => {
            commit(SET_DIARY_ENTRIES, res);
            return res;
        });
    }
};

const getters = {
    diaryTexts: state =>  state.texts,
    dailyComments: state =>state.dailyComments,
    myDailyComment: state => state.myDailyComment,
    analysisComments: state => state.analysisComments,
    myAnalysisComment: state => state.myAnalysisComment,
    diaryNotes: state => state.diaryNotes,
    myDiaryNotes: state => state.myDiaryNotes,
    diaryEntries: state => state.entries,
    diaryMedia: state => state.media,
    diary: state => state.diary || {},
    diaryWeek: state => state.diary_week,
    courseDiarySettings: state => state.course_settings,
    courseDiaryStatuses: state => state.course_statuses,
    courseDiaryMeals: state => state.course_meals,
    diaryErrorMessage: state => state.error,
    diaryDocuments: state => state.documents,
    diaryHolidays: state => state.holidays,
    diaryIntegrations: state => state.integrations,
    diaryLikes: state => state.diary_likes,
    leaveTypeOption: state => state.leaveTypeOption
};

export default {
    state,
    mutations,
    actions,
    getters
};
